import { Injectable, inject } from '@angular/core';
import FroalaEditor from 'froala-editor';
import { FroalaEditorOptions } from '@r1-compass-library/models';
import { FroalaEvent } from '@r1-compass-library/models/froala/froala-event';
import { FroalaActionsService } from '@r1-compass-library/services/froala/froala-actions.service';
import { FroalaEventsService } from '@r1-compass-library/services/froala/froala-events.service';
import { FroalaSettingsService } from '@r1-compass-library/services/froala/froala-settings.service';

/**
 * Decouples Froala editor events from Angular reactive form state. Consuming components provide
 * these callbacks to track genuine user interaction and counteract synthetic dirty/touched triggers,
 * without coupling this shared service to component DOM elements or FormControls.
 */
export interface FroalaInteractionCallbacks {
    onUserInteracted?: () => void;
    onEditorMousedown?: () => void;
    onEditorFocus?: () => void;
    onContentChanged?: () => void;
}

@Injectable({
    providedIn: 'root',
})
export class FroalaSetupService {
    // Keys that navigate, select, or change focus without modifying content. Excluded from
    // marking the editor as interacted so cursor navigation does not trigger a false-positive dirty state.
    static readonly nonModifyingKeys = new Set([
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Shift',
        'Control',
        'Alt',
        'Meta',
        'CapsLock',
        'Escape',
        'Tab',
        'PageUp',
        'PageDown',
        'Home',
        'End',
    ]);

    readonly #eventsService = inject(FroalaEventsService);
    readonly #settingsService = inject(FroalaSettingsService);
    readonly #actionsService = inject(FroalaActionsService);

    /**
     * Question: If we carry this pattern forward into a shared service, should we use flags to enable certain sets of options?
     * @returns The preset of options needed for the editor used in the content-library app (currently the app's only usage of the editor)
     */
    setupFroalaEditor(callbacks?: FroalaInteractionCallbacks): FroalaEditorOptions {
        let options = new FroalaEditorOptions();
        options = this.#settingsService.addBaseSettings(options);
        options = this.#settingsService.addContentSettings(options);
        options = this.#settingsService.addImageSettings(options);
        options = this.#settingsService.addTableSettings(options);
        options = this.setAppSpecificOptions(options);
        options = this.setEventHandlers(options, callbacks);
        options = this.reorderToolbarButtons(options);
        return options;
    }

    private setAppSpecificOptions(options: FroalaEditorOptions): FroalaEditorOptions {
        options.linkInsertButtons = [];
        options.zIndex = 2501;
        return options;
    }

    // Configures Froala events to distinguish modifying actions (typing content keys, paste, cut, drop,
    // table insert, undo/redo) from non-modifying interactions (mousedown, focus, navigation keys).
    // Modifying events invoke onUserInteracted, while non-modifying events notify the caller to counteract
    // synthetic touched/dirty triggers. Handlers are wired on options.events and also in Initialized
    // (via this.events.on) to ensure listener attachment across Froala lifecycle modes.
    private setEventHandlers(
        options: FroalaEditorOptions,
        callbacks?: FroalaInteractionCallbacks
    ): FroalaEditorOptions {
        const eventsService = this.#eventsService;
        const actionsService = this.#actionsService;
        const nonModifyingKeys = FroalaSetupService.nonModifyingKeys;
        const markInteracted = () => {
            callbacks?.onUserInteracted?.();
        };

        options.events = {};

        options.events[FroalaEvent.Mousedown] = function () {
            callbacks?.onEditorMousedown?.();
        };

        options.events[FroalaEvent.Focus] = function () {
            callbacks?.onEditorFocus?.();
        };

        options.events[FroalaEvent.ContentChanged] = function () {
            callbacks?.onContentChanged?.();
        };

        options.events[FroalaEvent.Keydown] = function (event: unknown) {
            const keyEvent = event as KeyboardEvent | undefined;
            if (keyEvent?.key && nonModifyingKeys.has(keyEvent.key)) {
                return;
            }
            markInteracted();
        };

        options.events[FroalaEvent.Input] = function () {
            markInteracted();
        };

        options.events[FroalaEvent.PasteAfter] = function () {
            markInteracted();
        };

        options.events[FroalaEvent.PasteAfterCleanup] = function () {
            markInteracted();
        };

        options.events[FroalaEvent.PasteWordPaste] = function () {
            markInteracted();
        };

        options.events[FroalaEvent.Drop] = function () {
            markInteracted();
        };

        options.events[FroalaEvent.ElementDropped] = function () {
            markInteracted();
        };

        options.events[FroalaEvent.WindowCut] = function () {
            markInteracted();
        };

        options.events[FroalaEvent.QuickInsertCommandsAfter] = function () {
            markInteracted();
        };

        options.events[FroalaEvent.TableInserted] = function () {
            markInteracted();
        };

        options.events[FroalaEvent.CommandsUndo] = function () {
            markInteracted();
        };

        options.events[FroalaEvent.CommandsRedo] = function () {
            markInteracted();
        };

        options.events[FroalaEvent.CommandsAfter] = function (
            this: FroalaEditor,
            cmd: string,
            _param1: unknown,
            _param2: unknown
        ) {
            markInteracted();
            eventsService.addFieldMergeDeleteBehavior(this, cmd, actionsService, () => options.fieldMergeDeleted);
            eventsService.addImageAdjustmentBehavior(this, cmd);
        };

        options.events[FroalaEvent.Initialized] = function (this: FroalaEditor) {
            eventsService.overrideFroalaDragDropBehavior(
                this,
                actionsService,
                () => options.fieldMergeDropped,
                () => options.fieldMergeDeleted
            );
            eventsService.overrideFroalaTableInsertBehavior(this, actionsService);

            if (this.events?.on) {
                this.events.on(FroalaEvent.Mousedown, () => callbacks?.onEditorMousedown?.());
                this.events.on(FroalaEvent.Focus, () => callbacks?.onEditorFocus?.());
                this.events.on(FroalaEvent.ContentChanged, () => callbacks?.onContentChanged?.());

                this.events.on(FroalaEvent.Keydown, (...args: unknown[]) => {
                    const evt = args.find(a => a instanceof KeyboardEvent) as KeyboardEvent | undefined;
                    if (evt?.key && nonModifyingKeys.has(evt.key)) {
                        return;
                    }
                    markInteracted();
                });
                this.events.on(FroalaEvent.Input, () => markInteracted());
                this.events.on(FroalaEvent.PasteAfter, () => markInteracted());
                this.events.on(FroalaEvent.Drop, () => markInteracted());
                this.events.on(FroalaEvent.CommandsAfter, () => markInteracted());
            }
        };
        options.events[FroalaEvent.CommandsAfter] = function (cmd: string, param1: any, param2: any) {
            const editor = this;
            eventsService.addFieldMergeDeleteBehavior(editor, cmd, actionsService, () => options.fieldMergeDeleted);
            eventsService.addImageAdjustmentBehavior(editor, cmd);
        };
        options.events[FroalaEvent.HtmlSet] = function () {
            const editor = this;
            eventsService.bindFieldMergeRemoveButtons(editor, actionsService, () => options.fieldMergeDeleted);
        };
        return options;
    }

    private reorderToolbarButtons(froalaOptions: FroalaEditorOptions): FroalaEditorOptions {
        // image-insert button should be between quote and insert-link buttons
        const imageInsertIndex = froalaOptions.toolbarButtons?.indexOf('insertImage');
        if (imageInsertIndex && imageInsertIndex >= 0) {
            const quoteIndex = froalaOptions.toolbarButtons?.indexOf('quote');
            if (quoteIndex && quoteIndex >= 0) {
                const imageInsertButton = froalaOptions.toolbarButtons?.splice(imageInsertIndex, 1) ?? [];
                froalaOptions.toolbarButtons?.splice(quoteIndex + 1, 0, ...imageInsertButton);
            }
        }

        // table-insert button should be after HTML
        const tableInsertIndex = froalaOptions.toolbarButtons?.indexOf('insertTable');
        if (tableInsertIndex && tableInsertIndex >= 0) {
            const quoteIndex = froalaOptions.toolbarButtons?.indexOf('html');
            if (quoteIndex && quoteIndex >= 0) {
                const imageInsertButton = froalaOptions.toolbarButtons?.splice(tableInsertIndex, 1) ?? [];
                froalaOptions.toolbarButtons?.splice(quoteIndex + 1, 0, ...imageInsertButton);
            }
        }
        return froalaOptions;
    }
}
